import { useCallback, useEffect, useState } from 'react';
import API_CONFIG from '../lib/config';
import { GiftItem } from '../types';

interface ItemsResponse {
  success: boolean;
  nextCursor: string | null;
  data: GiftItem[];
}

interface UseSubscriptionCatalogResult {
  items: GiftItem[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const SUBSCRIPTION_CATALOG_ENDPOINT = `${API_CONFIG.SUBSCRIPTION_BASE_URL}/items?limit=50`;

let cachedSubscriptionCatalog: GiftItem[] | null = null;

export const clearSubscriptionCatalogCache = (): void => {
  cachedSubscriptionCatalog = null;
};

export function useSubscriptionCatalog(): UseSubscriptionCatalogResult {
  const [items, setItems] = useState<GiftItem[]>(cachedSubscriptionCatalog ?? []);
  const [loading, setLoading] = useState(cachedSubscriptionCatalog === null);
  const [error, setError] = useState<string | null>(null);

  const fetchCatalog = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && cachedSubscriptionCatalog !== null) {
      setItems(cachedSubscriptionCatalog);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(SUBSCRIPTION_CATALOG_ENDPOINT);
      if (!response.ok) {
        throw new Error(`Failed to load subscription catalog: ${response.statusText}`);
      }

      const payload: ItemsResponse = await response.json();
      if (!payload.success) {
        throw new Error('The subscription catalog is currently unavailable.');
      }

      const normalizedItems = (payload.data ?? []).map((item) => ({
        ...item,
        id: item._id ?? item.id,
      }));

      cachedSubscriptionCatalog = normalizedItems;
      setItems(normalizedItems);
    } catch (catalogError) {
      const message =
        catalogError instanceof Error
          ? catalogError.message
          : 'We could not load more items right now.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchCatalog();
  }, [fetchCatalog]);

  return {
    items,
    loading,
    error,
    refetch: () => fetchCatalog(true),
  };
}
